import type { LoginFormCopy } from '@/components/auth/login'

export type AppleMemeMakerLocale = 'en' | 'es'

type StepCopy = {
    label: string
    mobileLabel: readonly [string, string]
    number: number
    stroke: string
}

type AppleMemeMakerCopy = {
    auth: {
        accountPrompt: string
        login: LoginFormCopy
        signUp: string
        title: string
    }
    campaign: {
        description: string
        downloadFilename: string
        header: string
        highlightedHeaderWord: string
        mobileSubtitle?: readonly string[]
        subtitle: readonly string[]
        title: string
    }
    create: {
        back: string
    }
    editor: {
        controls: {
            back: string
            download: string
            next: string
            preparing: string
            selectCaption: string
            share: string
            shareGif: string
            uploadTo: string
            uploadToGiphy: string
        }
        dragDropOverlay: {
            compactLabel: string
            label: string
        }
        errors: {
            addStickerBeforeUpload: string
            createGif: string
            downloadGif: string
            editorRender: string
            gifDimensions: string
            loginBeforeUpload: string
            prepareGif: string
            shareGif: string
            shareUnsupported: string
            upload: string
            uploadedGifUrl: string
            username: string
        }
        renderedMeme: {
            alt: string
            header: string
            ready: string
            readyIOS: string
            shareReady: string
            shareReadyIOS: string
            subheader: string
        }
        sticker: {
            delete: string
            header: string
            subheader: string
        }
        uploadHelp: {
            lineOne: string
            lineTwo: string
            strong: string
        }
        uploadErrorModal: {
            action: string
            title: string
        }
        uploadPhase: {
            finalizing: {
                label: string
                progress: number
            }
            submitting: {
                label: string
                progress: number
            }
        }
    }
    grid: {
        chooseGifToBegin: string
        next: string
    }
    steps: readonly [StepCopy, StepCopy, StepCopy]
}

export const appleMemeMakerCopyByLocale: Record<AppleMemeMakerLocale, AppleMemeMakerCopy> = {
    en: {
        auth: {
            accountPrompt: 'Need an account?',
            login: {
                appleLogin: 'Log in with Apple',
                emailPlaceholder: 'Email Address',
                errors: {
                    refreshSession: 'Login succeeded, but we could not refresh your session. Please try again.',
                    requestFailed: 'We could not log you in. Please try again.',
                    timeout: 'Login is taking longer than expected. Please try again.',
                },
                facebookLogin: 'Log in with Facebook',
                forgotPassword: 'Forgot Your Password?',
                passwordPlaceholder: 'Password',
                passwordVisibility: {
                    hide: 'Hide password.',
                    show: 'Show password as plain text. Warning: this will display your password on the screen.',
                },
                socialAuth: {
                    checkLogin: 'Check login',
                    finishedPrompt: 'Finished logging in?',
                    pending: 'Complete login in the popup window.',
                },
                submit: 'Log In',
                terms: {
                    conjunction: 'and',
                    prefix: "By logging in you agree to GIPHY's",
                    privacyPolicy: 'Privacy Policy',
                    termsOfService: 'Terms of Service',
                },
            },
            signUp: 'Sign up',
            title: 'Log in to upload',
        },
        campaign: {
            description: 'Choose a campaign background, add a caption sticker, and save or upload your GIF.',
            downloadFilename: 'apple-meme-maker.gif',
            header: 'Make Your Meme.',
            highlightedHeaderWord: 'Meme',
            subtitle: [
                'Create a custom GIF worth sharing with Apple + GIPHY.',
                'Made for reactions, group chats, & stories',
            ],
            title: 'Apple Meme Maker',
        },
        create: {
            back: 'Back',
        },
        editor: {
            controls: {
                back: 'Back',
                download: 'Download',
                next: 'Next',
                preparing: 'Preparing...',
                selectCaption: 'Select a Caption',
                share: 'Share',
                shareGif: 'Share GIF',
                uploadTo: 'Upload to',
                uploadToGiphy: 'Upload to GIPHY',
            },
            dragDropOverlay: {
                compactLabel: 'Drag & Drop anywhere!',
                label: 'Drag & Drop this GIF anywhere!',
            },
            errors: {
                addStickerBeforeUpload: 'Add a sticker before uploading.',
                createGif: 'Unable to create GIF.',
                downloadGif: 'Unable to download GIF.',
                editorRender:
                    "We couldn't start the meme editor. Turn on graphics acceleration in your browser settings, then reload.",
                gifDimensions: 'Unable to find GIF dimensions.',
                loginBeforeUpload: 'Log in before uploading.',
                prepareGif: 'Unable to prepare GIF.',
                shareGif: 'Unable to share GIF. Try downloading it instead.',
                shareUnsupported: 'Sharing GIFs is not supported in this browser. Try downloading it instead.',
                upload: "We couldn't upload your GIF. Please try again.",
                uploadedGifUrl: "Your GIF uploaded, but we couldn't open it. Please check your GIPHY profile.",
                username: 'Unable to find your GIPHY username.',
            },
            renderedMeme: {
                alt: 'Your generated GIF',
                header: 'Share your Creation',
                ready: 'Your GIF is ready.',
                readyIOS: 'Your GIF is ready. Long-press the GIF to save or copy.',
                shareReady: 'Your GIF is ready. Click Share again.',
                shareReadyIOS: 'Your GIF is ready. Tap Share again or long-press the GIF.',
                subheader: 'Your meme is fully baked. Now share it with the world.',
            },
            sticker: {
                delete: 'Delete',
                header: 'Add a Caption',
                subheader: 'Choose a caption for your meme and place it anywhere on your creation',
            },
            uploadHelp: {
                lineOne: 'to make your meme searchable across the GIPHY network.',
                lineTwo: 'Perfect for reactions in your favorite chat apps, stories, & more.',
                strong: 'Upload to GIPHY',
            },
            uploadErrorModal: {
                action: 'OK',
                title: 'Upload failed',
            },
            uploadPhase: {
                finalizing: {
                    label: 'Opening GIF',
                    progress: 94,
                },
                submitting: {
                    label: 'Saving Meme',
                    progress: 72,
                },
            },
        },
        grid: {
            chooseGifToBegin: 'Choose a GIF to begin',
            next: 'Next',
        },
        steps: [
            {
                label: 'Choose a GIF',
                mobileLabel: ['Choose', 'GIF'],
                number: 1,
                stroke: 'linear-gradient(135deg, #FF6666, #FFF35C)',
            },
            {
                label: 'Add a caption',
                mobileLabel: ['Add a', 'caption'],
                number: 2,
                stroke: 'linear-gradient(135deg, #9013FE, #E646B6)',
            },
            {
                label: 'Share it anywhere',
                mobileLabel: ['Share it', 'anywhere'],
                number: 3,
                stroke: 'linear-gradient(135deg, #00CCFF, #5D5FEF)',
            },
        ],
    },
    es: {
        auth: {
            accountPrompt: '¿Necesitas una cuenta?',
            login: {
                appleLogin: 'Inicia sesión con Apple',
                emailPlaceholder: 'Correo electrónico',
                errors: {
                    refreshSession: 'Iniciaste sesión, pero no pudimos actualizar tu sesión. Inténtalo de nuevo.',
                    requestFailed: 'No pudimos iniciar sesión. Inténtalo de nuevo.',
                    timeout: 'El inicio de sesión está tardando más de lo esperado. Inténtalo de nuevo.',
                },
                facebookLogin: 'Inicia sesión con Facebook',
                forgotPassword: '¿Olvidaste tu contraseña?',
                passwordPlaceholder: 'Contraseña',
                passwordVisibility: {
                    hide: 'Ocultar contraseña.',
                    show: 'Mostrar contraseña como texto. Advertencia: tu contraseña aparecerá en la pantalla.',
                },
                socialAuth: {
                    checkLogin: 'Verificar inicio de sesión',
                    finishedPrompt: '¿Terminaste de iniciar sesión?',
                    pending: 'Completa el inicio de sesión en la ventana emergente.',
                },
                submit: 'Iniciar sesión',
                terms: {
                    conjunction: 'y la',
                    prefix: 'Al iniciar sesión, aceptas los',
                    privacyPolicy: 'Política de Privacidad',
                    termsOfService: 'Términos de Servicio',
                },
            },
            signUp: 'Regístrate',
            title: 'Inicia sesión para subir',
        },
        campaign: {
            description: 'Elige una escena de Nido de Villanas, agrega una frase y comparte el drama.',
            downloadFilename: 'apple-meme-maker.gif',
            header: 'Arma tu drama',
            highlightedHeaderWord: 'drama',
            mobileSubtitle: ['Elige una escena de Nido de Villanas, agrega una frase y', 'comparte el drama.'],
            subtitle: ['Elige una escena de Nido de Villanas, agrega una frase y comparte el drama.'],
            title: 'Arma tu drama',
        },
        create: {
            back: 'Atrás',
        },
        editor: {
            controls: {
                back: 'Atrás',
                download: 'Descargar',
                next: 'Siguiente',
                preparing: 'Preparando...',
                selectCaption: 'Elige un texto',
                share: 'Compartir',
                shareGif: 'Compartir GIF',
                uploadTo: 'Subir a',
                uploadToGiphy: 'Subir a GIPHY',
            },
            dragDropOverlay: {
                compactLabel: 'Arrastra y suelta donde quieras',
                label: 'Arrastra y suelta este GIF donde quieras',
            },
            errors: {
                addStickerBeforeUpload: 'Agrega un sticker antes de subir.',
                createGif: 'No se pudo crear el GIF.',
                downloadGif: 'No pudimos descargar el GIF.',
                editorRender:
                    'No pudimos iniciar el editor de memes. Activa la aceleración de gráficos en la configuración del navegador y vuelve a cargar.',
                gifDimensions: 'No pudimos encontrar las dimensiones del GIF.',
                loginBeforeUpload: 'Inicia sesión antes de subir.',
                prepareGif: 'No pudimos preparar el GIF.',
                shareGif: 'No pudimos compartir el GIF. Intenta descargarlo.',
                shareUnsupported: 'Este navegador no permite compartir GIFs. Intenta descargarlo.',
                upload: 'No pudimos subir tu GIF. Inténtalo de nuevo.',
                uploadedGifUrl: 'Tu GIF se subió, pero no pudimos abrirlo. Revisa tu perfil de GIPHY.',
                username: 'No pudimos encontrar tu usuario de GIPHY.',
            },
            renderedMeme: {
                alt: 'Tu GIF generado',
                header: 'Comparte el drama',
                ready: 'Tu GIF está listo.',
                readyIOS: 'Tu GIF está listo. Mantén presionado el GIF para guardarlo o copiarlo.',
                shareReady: 'Tu GIF está listo. Haz clic en Compartir otra vez.',
                shareReadyIOS: 'Tu GIF está listo. Toca Compartir otra vez o mantén presionado el GIF.',
                subheader: 'Tu creación está lista para ser compartida con el mundo.',
            },
            sticker: {
                delete: 'Eliminar',
                header: 'Agrega una frase',
                subheader: 'Elige una frase para tu meme y colócala donde prefieras.',
            },
            uploadHelp: {
                lineOne: 'para que tu meme se pueda buscar en toda la red de GIPHY.',
                lineTwo: 'Perfecto para reacciones en tus apps de chat favoritas, historias y más.',
                strong: 'Subir a GIPHY',
            },
            uploadErrorModal: {
                action: 'OK',
                title: 'No se pudo subir',
            },
            uploadPhase: {
                finalizing: {
                    label: 'Abriendo GIF',
                    progress: 94,
                },
                submitting: {
                    label: 'Guardando meme',
                    progress: 72,
                },
            },
        },
        grid: {
            chooseGifToBegin: 'Elige la escena',
            next: 'Siguiente',
        },
        steps: [
            {
                label: 'Elige la escena',
                mobileLabel: ['Elige', 'escena'],
                number: 1,
                stroke: 'linear-gradient(135deg, #FF6666, #FFF35C)',
            },
            {
                label: 'Agrega una frase',
                mobileLabel: ['Agrega', 'frase'],
                number: 2,
                stroke: 'linear-gradient(135deg, #9013FE, #E646B6)',
            },
            {
                label: 'Comparte el drama',
                mobileLabel: ['Comparte', 'drama'],
                number: 3,
                stroke: 'linear-gradient(135deg, #00CCFF, #5D5FEF)',
            },
        ],
    },
}

export const appleMemeMakerCopy = appleMemeMakerCopyByLocale.es
